import { Cloud, CloudRain, Snowflake, Sun, Loader2 } from 'lucide-react';
import { useCurrentWeather } from '@/hooks/useCurrentWeather';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip';
import { format } from 'date-fns';

export function WeatherPill() {
  const { weather, isLoading, hasLocation } = useCurrentWeather();

  // Don't render if no location configured
  if (!hasLocation) return null;

  const getIcon = () => {
    if (isLoading) return <Loader2 className="h-4 w-4 animate-spin" />;
    if (!weather) return <Cloud className="h-4 w-4" />;
    
    switch (weather.condition) {
      case 'snowy': return <Snowflake className="h-4 w-4" />;
      case 'rainy': return <CloudRain className="h-4 w-4" />;
      case 'cloudy': return <Cloud className="h-4 w-4" />;
      case 'sunny': return <Sun className="h-4 w-4" />;
      default: return <Cloud className="h-4 w-4" />;
    }
  };

  const temperature = weather ? `${Math.round(weather.temperature_f)}°F` : '--°';
  // CORE-BR: the tooltip states when the reading was actually obtained, and
  // says so plainly when that could not be established or when the reading
  // is being held over from before a failed refresh. A time here that was
  // really "the moment this component parsed the response" told the viewer
  // nothing about the age of the number beside it.
  const tooltipText = !weather
    ? 'Weather unavailable'
    : weather.isStale
      ? weather.reportedAt
        ? `Out of date — last obtained ${format(weather.reportedAt, 'h:mm a')}`
        : 'Out of date — could not refresh'
      : weather.reportedAt
        ? `Obtained ${format(weather.reportedAt, 'h:mm a')}`
        : 'Reading time unknown';

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="flex items-center gap-1 px-2 py-1 rounded-md bg-muted/50 text-sm text-muted-foreground hover:bg-muted/80 transition-colors cursor-default">
            {getIcon()}
            <span className="font-medium">{temperature}</span>
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <p>{tooltipText}</p>
          {weather?.description && <p className="text-xs opacity-75">{weather.description}</p>}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
